import axios from 'axios'
import { getResponseData, normalizeQueryError } from 'src/queries/network.utils'
import { getRequestConfig, RequestConfig } from 'src/queries/requestConfig'

import {
  AccountCollectionFlowCompletionResponse,
  AccountCollectionFlowResponse,
  BillPaymentDetailsResponse,
  BillPaymentError,
  BillPaymentInvoiceAttachmentResponse,
  BillPaymentResponse,
  CancelBillPaymentAttempt,
  CompleteFundingAccountVerificationRequest,
  ConfirmBillPaymentAttempt,
  CreatePaymentConnectionRequest,
  CreatePaymentConnectionResponse,
  FundingAccountVerificationResponse,
  PaymentConnectionResponse,
  PaymentFundingAccountDetails,
  ResendBillPaymentAuthorizationAttempt,
  RestartBillPaymentAuthorizationAttempt,
  ScheduleBillPaymentAttempt,
  UploadBillPaymentInvoiceAttachmentAttempt,
} from './payments.types'
import { extractKybValidationError } from './paymentsKybError'

const CONNECTION_BASE = '/payments/connection'
const ACCOUNT_COLLECTION_FLOW_PATH = '/payments/account-collection-flow'
const FUNDING_ACCOUNT_PATH = '/payments/funding-account'
const FUNDING_ACCOUNT_VERIFICATION_PATH =
  '/payments/funding-account/verification'
const PAYMENT_INTENTS_PATH = '/payments/payment-intents'
const INVOICE_ATTACHMENTS_PATH = '/payments/invoice-attachments'
const IMF_FIXDATE = /^(Sun|Mon|Tue|Wed|Thu|Fri|Sat), (0[1-9]|[12]\d|3[01]) (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) (\d{4}) ([01]\d|2[0-3]):([0-5]\d):([0-5]\d) GMT$/

const parseImfFixdate = (value: string): number | undefined => {
  if (!IMF_FIXDATE.test(value)) {
    return undefined
  }
  const parsed = Date.parse(value)
  if (!Number.isFinite(parsed) || new Date(parsed).toUTCString() !== value) {
    return undefined
  }
  return parsed
}

const billPaymentError = (caught: unknown): BillPaymentError => {
  const normalized = normalizeQueryError(caught) as BillPaymentError
  const original = normalized.originalError
  const retryAfter = axios.isAxiosError(original)
    ? original.response?.headers?.['retry-after']
    : undefined
  const seconds = Number(retryAfter)
  if (Number.isFinite(seconds)) {
    if (seconds > 0) {
      normalized.retryAfterSeconds = Math.ceil(seconds)
    }
    return normalized
  }
  if (typeof retryAfter === 'string') {
    const retryAfterDate = parseImfFixdate(retryAfter)
    if (retryAfterDate !== undefined) {
      const remainingSeconds = (retryAfterDate - Date.now()) / 1000
      if (remainingSeconds > 0) {
        normalized.retryAfterSeconds = Math.ceil(remainingSeconds)
      }
    }
  }
  return normalized
}

const mutationConfig = (
  idempotencyKey: string,
  revision?: number,
): ReturnType<typeof getRequestConfig> => {
  const config = getRequestConfig(RequestConfig.Default)
  return {
    ...config,
    headers: {
      ...config.headers,
      'Idempotency-Key': idempotencyKey,
      ...(revision === undefined ? {} : { 'If-Match': `"${revision}"` }),
    },
  }
}

const multipartMutationConfig = (
  idempotencyKey: string,
): ReturnType<typeof getRequestConfig> => {
  const config = getRequestConfig(RequestConfig.Form)
  return {
    ...config,
    headers: {
      ...config.headers,
      'Idempotency-Key': idempotencyKey,
    },
  }
}

const requireId = (value: string, operation: string, name: string): void => {
  if (!value.trim()) {
    throw new Error(`${operation} requires a ${name}`)
  }
}

const requirePositiveRevision = (revision: number, operation: string): void => {
  if (!Number.isInteger(revision) || revision <= 0) {
    throw new Error(`${operation} requires a positive revision`)
  }
}

const requireIdempotencyKey = (key: string, operation: string): void => {
  if (!key.trim() || key.length > 255) {
    throw new Error(`${operation} requires a valid idempotencyKey`)
  }
}

const PAYMENT_STATES = [
  'DRAFT',
  'AWAITING_AUTHORIZATION',
  'SCHEDULED',
  'CANCELLED',
] as const
const AUTHORIZATION_STATES = [
  'PENDING',
  'CONSUMED',
  'LOCKED',
  'EXPIRED',
  'CANCELLED',
] as const
const DELIVERY_STATES = ['PENDING', 'SENT', 'FAILED'] as const
const ATTACHMENT_ASSOCIATION_STATES = ['UNATTACHED', 'ATTACHED'] as const
const ATTACHMENT_SCAN_STATES = ['PENDING', 'CLEAN', 'INFECTED'] as const
const ATTACHMENT_CONTENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
] as const

type WireRecord = Record<string, unknown>

const isRecord = (value: unknown): value is WireRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0
const isNullableString = (value: unknown): value is string | null =>
  value === null || typeof value === 'string'
const isPositiveInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0
const isNonNegativeInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0
const isTimestamp = (value: unknown): value is string =>
  isNonEmptyString(value) && !Number.isNaN(Date.parse(value))
const isNullableTimestamp = (value: unknown): value is string | null =>
  value === null || isTimestamp(value)
const isNullableDate = (value: unknown): value is string | null =>
  value === null ||
  (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value))
const isOneOf = <T extends string>(
  value: unknown,
  allowed: readonly T[],
): value is T =>
  typeof value === 'string' &&
  (allowed as readonly string[]).includes(value)

const invalidResponse = (
  errorCode: string,
  error: string,
  paymentId?: string,
): BillPaymentError => ({ error, errorCode, status: 200, paymentId })

const isResponseValidationError = (error: unknown): error is BillPaymentError =>
  isRecord(error) &&
  typeof error.errorCode === 'string' &&
  error.errorCode.endsWith('_RESPONSE_INVALID')

const validAttachmentSummary = (value: unknown): boolean =>
  isRecord(value) &&
  isNonEmptyString(value.id) &&
  isOneOf(value.associationStatus, ATTACHMENT_ASSOCIATION_STATES) &&
  isOneOf(value.scanStatus, ATTACHMENT_SCAN_STATES)

const validatePaymentResponse = (
  value: unknown,
  errorCode: string,
  operation: string,
  expected?: { id?: string; revision?: number; state?: string },
): BillPaymentResponse => {
  const paymentId = isRecord(value) && isNonEmptyString(value.id) ? value.id : undefined
  if (expected?.id !== undefined && paymentId !== expected.id) {
    throw invalidResponse(
      errorCode,
      `${operation} returned the wrong payment id`,
      paymentId,
    )
  }
  if (
    expected?.revision !== undefined &&
    (!isRecord(value) || value.revision !== expected.revision)
  ) {
    throw invalidResponse(
      errorCode,
      `${operation} returned the wrong payment revision`,
      paymentId,
    )
  }
  if (
    expected?.state !== undefined &&
    (!isRecord(value) || value.state !== expected.state)
  ) {
    throw invalidResponse(
      errorCode,
      `${operation} did not return ${expected.state}`,
      paymentId,
    )
  }
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.id) ||
    !isNonEmptyString(value.vendorId) ||
    !isNonEmptyString(value.vendorPaymentMethodId) ||
    !isNonEmptyString(value.vendorName) ||
    !isNullableString(value.vendorRemittanceEmailHint) ||
    !isPositiveInteger(value.revision) ||
    !isOneOf(value.state, PAYMENT_STATES) ||
    !isPositiveInteger(value.amountCents) ||
    value.currency !== 'USD' ||
    !isNonEmptyString(value.invoiceNumber) ||
    !isNullableDate(value.invoiceDate) ||
    !isNullableDate(value.invoiceDueDate) ||
    !isNullableString(value.memo) ||
    !(
      value.invoiceAttachment === null ||
      validAttachmentSummary(value.invoiceAttachment)
    ) ||
    !isTimestamp(value.eligibleAt) ||
    !isNonEmptyString(value.acknowledgementCopyVersion) ||
    !isTimestamp(value.createdAt) ||
    !isTimestamp(value.updatedAt) ||
    !isNullableTimestamp(value.authorizedAt) ||
    !isNullableTimestamp(value.scheduledAt) ||
    !isNullableTimestamp(value.cancelledAt)
  ) {
    throw invalidResponse(
      errorCode,
      `${operation} returned an invalid payment response`,
      paymentId,
    )
  }

  if (
    (value.state === 'AWAITING_AUTHORIZATION' &&
      (value.authorizedAt !== null ||
        value.scheduledAt !== null ||
        value.cancelledAt !== null)) ||
    (value.state === 'SCHEDULED' &&
      (!isTimestamp(value.authorizedAt) ||
        !isTimestamp(value.scheduledAt) ||
        value.cancelledAt !== null)) ||
    (value.state === 'CANCELLED' && !isTimestamp(value.cancelledAt))
  ) {
    throw invalidResponse(
      errorCode,
      `${operation} returned timestamps inconsistent with payment state`,
      value.id,
    )
  }
  return value as unknown as BillPaymentResponse
}

const validateAuthorizationResponse = (
  value: unknown,
  errorCode: string,
  operation: string,
  expectedId?: string,
): BillPaymentDetailsResponse['authorization'] => {
  if (value === null) return null
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.authorizationId) ||
    value.method !== 'EMAIL' ||
    !isOneOf(value.state, AUTHORIZATION_STATES) ||
    !isNullableString(value.destinationHint) ||
    !isTimestamp(value.expiresAt) ||
    !isNonNegativeInteger(value.attemptsRemaining) ||
    !isTimestamp(value.resendAvailableAt) ||
    !isOneOf(value.deliveryState, DELIVERY_STATES) ||
    (expectedId !== undefined && value.authorizationId !== expectedId)
  ) {
    throw invalidResponse(
      errorCode,
      `${operation} returned an invalid authorization response`,
    )
  }
  return value as unknown as BillPaymentDetailsResponse['authorization']
}

const validateDetailsResponse = (
  value: unknown,
  errorCode: string,
  operation: string,
  expected?: {
    paymentId?: string
    revision?: number
    paymentState?: string
    authorizationId?: string
    requirePendingAuthorization?: boolean
  },
): BillPaymentDetailsResponse => {
  if (!isRecord(value) || !('payment' in value) || !('authorization' in value)) {
    throw invalidResponse(errorCode, `${operation} returned an invalid envelope`)
  }
  const payment = validatePaymentResponse(value.payment, errorCode, operation, {
    id: expected?.paymentId,
    revision: expected?.revision,
    state: expected?.paymentState,
  })
  const authorization = validateAuthorizationResponse(
    value.authorization,
    errorCode,
    operation,
    expected?.authorizationId,
  )
  if (
    expected?.requirePendingAuthorization &&
    (!authorization || authorization.state !== 'PENDING')
  ) {
    throw invalidResponse(
      errorCode,
      `${operation} did not return a pending authorization`,
      payment.id,
    )
  }
  return { payment, authorization }
}

const validateAttachmentResponse = (
  value: unknown,
  operation: string,
  expectedId?: string,
): BillPaymentInvoiceAttachmentResponse => {
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.id) ||
    (expectedId !== undefined && value.id !== expectedId) ||
    !isNonEmptyString(value.filename) ||
    !isOneOf(value.contentType, ATTACHMENT_CONTENT_TYPES) ||
    !isPositiveInteger(value.sizeBytes) ||
    !isOneOf(value.associationStatus, ATTACHMENT_ASSOCIATION_STATES) ||
    !isOneOf(value.scanStatus, ATTACHMENT_SCAN_STATES) ||
    !isTimestamp(value.createdAt) ||
    !isNullableTimestamp(value.expiresAt) ||
    !isNullableString(value.downloadUrl)
  ) {
    throw invalidResponse(
      'PAYMENT_ATTACHMENT_RESPONSE_INVALID',
      `${operation} returned an invalid attachment response`,
    )
  }
  return value as unknown as BillPaymentInvoiceAttachmentResponse
}

// ─── Connection & KYB ────────────────────────────────────────────

export async function getPaymentConnection(): Promise<PaymentConnectionResponse> {
  return getResponseData(
    axios.get<PaymentConnectionResponse>(
      CONNECTION_BASE,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export async function getFundingAccount(): Promise<PaymentFundingAccountDetails> {
  return getResponseData(
    axios.get<PaymentFundingAccountDetails>(
      FUNDING_ACCOUNT_PATH,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

/** `POST /payments/connection` — KYB-ONLY (the split flow). The full KYB PII
 * (SSNs, DOBs, beneficial-owner identities, document bytes) transits to the
 * provider on this single call and is never persisted, logged, or cached; only
 * `legalEntityId` + `kybStatus` come back on the summary (ADR-0005). */
export async function submitLegalEntity(
  request: CreatePaymentConnectionRequest,
): Promise<CreatePaymentConnectionResponse> {
  try {
    const response = await axios.post<CreatePaymentConnectionResponse>(
      CONNECTION_BASE,
      request,
      getRequestConfig(RequestConfig.Default),
    )
    return response.data
  } catch (error) {
    // A KYB validation 422 carries field-level errors the form must surface —
    // throw a typed error the mutation's onError branches on (never the silent
    // pending dead-end). Everything else falls back to the normalized error.
    const validationError = extractKybValidationError(error)
    if (validationError) throw validationError
    throw normalizeQueryError(error)
  }
}

/**
 * Bootstrap the embedded Modern Treasury Account Collection Flow for the
 * org's own funding (pay-from) account — `POST /payments/account-collection-flow`.
 * No routing/account number is sent here at all — the customer types their
 * bank details into MT's own iframe (`MtAccountCollection`), MT creates the
 * ExternalAccount, then the app calls `completeAccountCollectionFlow(flowId)` to
 * link it. The app only mints the flow's short-lived `clientToken` +
 * `publishableKey` with this call.
 */
export async function createAccountCollectionFlow(): Promise<AccountCollectionFlowResponse> {
  return getResponseData(
    axios.post<AccountCollectionFlowResponse>(
      ACCOUNT_COLLECTION_FLOW_PATH,
      undefined,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

/**
 * Complete the embedded flow — `POST /payments/account-collection-flow/{flowId}/
 * complete`. Called from the MT embed's `onSuccess` (MT fires no completion
 * webhook): the backend retrieves the flow from Modern Treasury and links the
 * funding External Account. Returns `{ status: 'COMPLETED', fundingAccount }`
 * (HTTP 200, linked — the connection now carries the funding account) or
 * `{ status: 'PENDING' }` (HTTP 202, MT has not finished — retry). HTTP 404
 * (unknown/foreign flow), 409 (KYB not active or the flow expired/cancelled),
 * and 502 (provider) surface through the normalized error channel.
 */
export async function completeAccountCollectionFlow(
  flowId: string,
): Promise<AccountCollectionFlowCompletionResponse> {
  // Guard the id in the fn (not just the call site): an empty id otherwise
  // builds `/account-collection-flow//complete` and fires it (see
  // src/queries/AGENTS.md).
  if (!flowId) {
    throw new Error('completeAccountCollectionFlow requires a flowId')
  }
  return getResponseData(
    axios.post<AccountCollectionFlowCompletionResponse>(
      `${ACCOUNT_COLLECTION_FLOW_PATH}/${encodeURIComponent(flowId)}/complete`,
      undefined,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export async function initiateFundingAccountVerification(): Promise<FundingAccountVerificationResponse> {
  return getResponseData(
    axios.post<FundingAccountVerificationResponse>(
      FUNDING_ACCOUNT_VERIFICATION_PATH,
      undefined,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export async function completeFundingAccountVerification(
  request: CompleteFundingAccountVerificationRequest,
): Promise<FundingAccountVerificationResponse> {
  const { amounts } = request
  if (
    amounts.length !== 2 ||
    amounts.some((amount) => !Number.isInteger(amount))
  ) {
    throw new Error(
      'completeFundingAccountVerification requires two whole-cent amounts',
    )
  }

  return getResponseData(
    axios.post<FundingAccountVerificationResponse>(
      `${FUNDING_ACCOUNT_VERIFICATION_PATH}/complete`,
      request,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export async function uploadBillPaymentInvoiceAttachment(
  attempt: UploadBillPaymentInvoiceAttachmentAttempt,
): Promise<BillPaymentInvoiceAttachmentResponse> {
  const { file, idempotencyKey } = attempt
  if (!file) {
    throw new Error('uploadBillPaymentInvoiceAttachment requires a file')
  }
  requireIdempotencyKey(idempotencyKey, 'uploadBillPaymentInvoiceAttachment')

  const formData = new FormData()
  formData.append('file', file)
  try {
    const response = await axios.post<BillPaymentInvoiceAttachmentResponse>(
      INVOICE_ATTACHMENTS_PATH,
      formData,
      multipartMutationConfig(idempotencyKey),
    )
    return validateAttachmentResponse(
      response.data,
      'uploadBillPaymentInvoiceAttachment',
    )
  } catch (error) {
    if (isResponseValidationError(error)) throw error
    throw billPaymentError(error)
  }
}

export async function getBillPaymentInvoiceAttachment(
  attachmentId: string,
): Promise<BillPaymentInvoiceAttachmentResponse> {
  requireId(
    attachmentId,
    'getBillPaymentInvoiceAttachment',
    'attachmentId',
  )
  try {
    const response = await axios.get<BillPaymentInvoiceAttachmentResponse>(
      `${INVOICE_ATTACHMENTS_PATH}/${encodeURIComponent(attachmentId)}`,
      getRequestConfig(RequestConfig.Default),
    )
    return validateAttachmentResponse(
      response.data,
      'getBillPaymentInvoiceAttachment',
      attachmentId,
    )
  } catch (error) {
    if (isResponseValidationError(error)) throw error
    throw billPaymentError(error)
  }
}

export async function deleteBillPaymentInvoiceAttachment(
  attachmentId: string,
): Promise<void> {
  requireId(
    attachmentId,
    'deleteBillPaymentInvoiceAttachment',
    'attachmentId',
  )
  try {
    const response = await axios.delete<void>(
      `${INVOICE_ATTACHMENTS_PATH}/${encodeURIComponent(attachmentId)}`,
      getRequestConfig(RequestConfig.Default),
    )
    return response.data
  } catch (error) {
    throw billPaymentError(error)
  }
}

export async function confirmBillPayment(
  attempt: ConfirmBillPaymentAttempt,
): Promise<BillPaymentDetailsResponse> {
  const { request, idempotencyKey } = attempt
  requireId(request.vendorId, 'confirmBillPayment', 'vendorId')
  requireIdempotencyKey(idempotencyKey, 'confirmBillPayment')
  try {
    const response = await axios.post<BillPaymentDetailsResponse>(
      `${PAYMENT_INTENTS_PATH}/confirm`,
      request,
      mutationConfig(idempotencyKey),
    )
    return validateDetailsResponse(
      response.data,
      'PAYMENT_CONFIRM_RESPONSE_INVALID',
      'confirmBillPayment',
      {
        paymentState: 'AWAITING_AUTHORIZATION',
        requirePendingAuthorization: true,
      },
    )
  } catch (error) {
    if (isResponseValidationError(error)) throw error
    throw billPaymentError(error)
  }
}

export async function getBillPayment(
  paymentId: string,
): Promise<BillPaymentDetailsResponse> {
  requireId(paymentId, 'getBillPayment', 'paymentId')
  try {
    const response = await axios.get<BillPaymentDetailsResponse>(
      `${PAYMENT_INTENTS_PATH}/${encodeURIComponent(paymentId)}`,
      getRequestConfig(RequestConfig.Default),
    )
    return validateDetailsResponse(
      response.data,
      'PAYMENT_GET_RESPONSE_INVALID',
      'getBillPayment',
      { paymentId },
    )
  } catch (error) {
    if (isResponseValidationError(error)) throw error
    throw billPaymentError(error)
  }
}

export async function restartBillPaymentAuthorization(
  attempt: RestartBillPaymentAuthorizationAttempt,
): Promise<BillPaymentDetailsResponse> {
  const { paymentId, revision, idempotencyKey } = attempt
  requireId(paymentId, 'restartBillPaymentAuthorization', 'paymentId')
  requirePositiveRevision(revision, 'restartBillPaymentAuthorization')
  requireIdempotencyKey(idempotencyKey, 'restartBillPaymentAuthorization')
  try {
    const response = await axios.post<BillPaymentDetailsResponse>(
      `${PAYMENT_INTENTS_PATH}/${encodeURIComponent(paymentId)}/authorizations`,
      undefined,
      mutationConfig(idempotencyKey, revision),
    )
    return validateDetailsResponse(
      response.data,
      'PAYMENT_RESTART_RESPONSE_INVALID',
      'restartBillPaymentAuthorization',
      {
        paymentId,
        revision,
        paymentState: 'AWAITING_AUTHORIZATION',
        requirePendingAuthorization: true,
      },
    )
  } catch (error) {
    if (isResponseValidationError(error)) throw error
    throw billPaymentError(error)
  }
}

export async function resendBillPaymentAuthorization(
  attempt: ResendBillPaymentAuthorizationAttempt,
): Promise<BillPaymentDetailsResponse> {
  const { paymentId, revision, authorizationId, idempotencyKey } = attempt
  requireId(paymentId, 'resendBillPaymentAuthorization', 'paymentId')
  requirePositiveRevision(revision, 'resendBillPaymentAuthorization')
  requireId(
    authorizationId,
    'resendBillPaymentAuthorization',
    'authorizationId',
  )
  requireIdempotencyKey(idempotencyKey, 'resendBillPaymentAuthorization')
  try {
    const response = await axios.post<BillPaymentDetailsResponse>(
      `${PAYMENT_INTENTS_PATH}/${encodeURIComponent(paymentId)}/authorizations/${encodeURIComponent(authorizationId)}/resend`,
      undefined,
      mutationConfig(idempotencyKey),
    )
    return validateDetailsResponse(
      response.data,
      'PAYMENT_RESEND_RESPONSE_INVALID',
      'resendBillPaymentAuthorization',
      {
        paymentId,
        revision,
        paymentState: 'AWAITING_AUTHORIZATION',
        authorizationId,
        requirePendingAuthorization: true,
      },
    )
  } catch (error) {
    if (isResponseValidationError(error)) throw error
    throw billPaymentError(error)
  }
}

export async function scheduleBillPayment(
  attempt: ScheduleBillPaymentAttempt,
): Promise<BillPaymentResponse> {
  const { paymentId, revision, authorizationId, code, idempotencyKey } = attempt
  requireId(paymentId, 'scheduleBillPayment', 'paymentId')
  requirePositiveRevision(revision, 'scheduleBillPayment')
  requireId(authorizationId, 'scheduleBillPayment', 'authorizationId')
  if (!/^\d{6}$/.test(code)) {
    throw new Error('scheduleBillPayment requires a six-digit code')
  }
  requireIdempotencyKey(idempotencyKey, 'scheduleBillPayment')

  try {
    const response = await axios.post<BillPaymentResponse>(
      `${PAYMENT_INTENTS_PATH}/${encodeURIComponent(paymentId)}/schedule`,
      { authorizationId, code },
      mutationConfig(idempotencyKey, revision),
    )

    return validatePaymentResponse(
      response.data,
      'PAYMENT_SCHEDULE_RESPONSE_INVALID',
      'scheduleBillPayment',
      { id: paymentId, revision: revision + 1, state: 'SCHEDULED' },
    )
  } catch (error) {
    if (isResponseValidationError(error)) throw error
    throw billPaymentError(error)
  }
}

export async function cancelBillPayment(
  attempt: CancelBillPaymentAttempt,
): Promise<BillPaymentResponse> {
  const { paymentId, revision, idempotencyKey } = attempt
  requireId(paymentId, 'cancelBillPayment', 'paymentId')
  requirePositiveRevision(revision, 'cancelBillPayment')
  requireIdempotencyKey(idempotencyKey, 'cancelBillPayment')
  try {
    const response = await axios.post<BillPaymentResponse>(
      `${PAYMENT_INTENTS_PATH}/${encodeURIComponent(paymentId)}/cancel`,
      undefined,
      mutationConfig(idempotencyKey, revision),
    )
    return validatePaymentResponse(
      response.data,
      'PAYMENT_CANCEL_RESPONSE_INVALID',
      'cancelBillPayment',
      { id: paymentId, revision: revision + 1, state: 'CANCELLED' },
    )
  } catch (error) {
    if (isResponseValidationError(error)) throw error
    throw billPaymentError(error)
  }
}
